import { useState } from "react";
import { motion } from "framer-motion";
import { Search, Trash2, Play, BookOpen, Award, Clock, User, Heart, Bookmark, Globe, FolderInput, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { MoveLectureDialog } from "@/components/MoveLectureDialog";
import { FounderBadge } from "@/components/FounderBadge";
import { useAuth } from "@/context/AuthContext";

const BANNER =
  "https://images.unsplash.com/photo-1741699428553-41c8e5bd894d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxzdHVkeSUyMGJvb2tzJTIwdW5pdmVyc2l0eSUyMGxpYnJhcnklMjBjb3p5JTIwcmVhZGluZyUyMGRlc2t8ZW58MHx8fHwxNzg4M105OEB0&ixlib=rb-4.1.0&q=85";

const scoreColor = (s) =>
  s >= 80 ? "hsl(var(--success))" : s >= 60 ? "hsl(var(--accent))" : "hsl(var(--destructive))";

// Truncate the AI-generated summary to a punchy ~60-word teaser so cards feel
// scannable rather than dumping the full executive summary. Splits on any run of
// whitespace and appends an ellipsis when trimmed.
const teaser = (text, max = 60) => {
  if (!text) return "";
  const words = text.trim().split(/\s+/);
  if (words.length <= max) return text.trim();
  return words.slice(0, max).join(" ").replace(/[,;:.!?—-]+$/, "") + "…";
};

export const Library = ({
  lectures,
  onOpen,
  onDelete,
  onLike,
  onSave,
  onMoved,
  overline = "Your Library",
  heading = "Saved Lectures",
  searchPlaceholder = "Search your library…",
  showAuthor = false,
  enableFilters = false,
}) => {
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [toMove, setToMove] = useState(null);
  const [sort, setSort] = useState("newest");
  const [langFilter, setLangFilter] = useState("all");
  const { user } = useAuth();
  const isInstructor = user && (user.role === "instructor" || user.role === "admin");

  const availableLanguages = enableFilters
    ? Array.from(new Set(lectures.map((l) => l.language || "English"))).sort()
    : [];

  let filtered = lectures.filter(
    (l) =>
      l.title.toLowerCase().includes(q.toLowerCase()) ||
      l.book_title.toLowerCase().includes(q.toLowerCase())
  );
  if (enableFilters && langFilter !== "all") {
    filtered = filtered.filter((l) => (l.language || "English") === langFilter);
  }
  if (enableFilters) {
    filtered = [...filtered].sort((a, b) =>
      sort === "liked"
        ? (b.like_count || 0) - (a.like_count || 0)
        : new Date(b.created_at) - new Date(a.created_at)
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="relative rounded-3xl overflow-hidden border border-border mb-8 h-44 sm:h-56">
        <img src={BANNER} alt="Library" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/30" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-10 text-primary-foreground">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] opacity-80">{overline}</span>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold">{heading}</h1>
          <p className="mt-1 opacity-90 text-sm">{lectures.length} lecture{lectures.length !== 1 && "s"} {showAuthor ? "from the community" : "archived"}</p>
        </div>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="library-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-full border border-input bg-card pl-11 pr-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring/60"
          />
        </div>
        {enableFilters && (
          <div className="flex items-center gap-2">
            <select
              data-testid="explore-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-full border border-input bg-card px-4 py-2.5 text-sm font-medium outline-none focus:ring-2 focus:ring-ring/60 cursor-pointer"
            >
              <option value="newest">Newest</option>
              <option value="liked">Most liked</option>
            </select>
            <select
              data-testid="explore-language-filter"
              value={langFilter}
              onChange={(e) => setLangFilter(e.target.value)}
              className="rounded-full border border-input bg-card px-4 py-2.5 text-sm font-medium outline-none focus:ring-2 focus:ring-ring/60 cursor-pointer max-w-[200px]"
            >
              <option value="all">All languages</option>
              {availableLanguages.map((lang) => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-20 text-center">
          <BookOpen className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-4 text-muted-foreground">
            {lectures.length === 0
              ? (showAuthor ? "No community creations yet — be the first to create one!" : "No lectures yet — create your first one!")
              : `No results for "${q}".`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((l, i) => (
            <motion.div
              key={l.id}
              data-testid="saved-lecture-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="group rounded-2xl border border-border bg-card p-6 shadow-sm hover:border-primary/50 hover:-translate-y-1 transition-all cursor-pointer flex flex-col"
              onClick={() => onOpen(l, "lecture")}
            >
              <div className="flex items-start justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-accent line-clamp-1">
                  {l.book_title}
                </span>
                {l.owned && l.best_score != null && (
                  <span
                    className="flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold text-white shrink-0"
                    style={{ background: scoreColor(l.best_score) }}
                  >
                    <Award className="h-3 w-3" /> {l.best_score}%
                  </span>
                )}
              </div>
              <h3 className="mt-2 font-serif text-xl font-semibold leading-snug line-clamp-2 flex-1">
                {l.title}
              </h3>
              {l.summary && (
                <div data-testid="card-teaser" className="mt-2 rounded-xl bg-secondary/40 border-l-2 border-accent px-3 py-2">
                  <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-accent mb-1">
                    <Sparkles className="h-2.5 w-2.5" /> AI teaser
                  </div>
                  <p className="text-sm text-foreground/75 leading-relaxed line-clamp-4">
                    {teaser(l.summary, 60)}
                  </p>
                </div>
              )}
              <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />
                  {new Date(l.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </span>
                {!l.owned && l.author_name && (
                  <span data-testid="lecture-author" className="flex items-center gap-1.5">
                    <User className="h-3 w-3" /> by {l.author_name}
                    {l.author_is_founder && <FounderBadge className="ml-1" />}
                  </span>
                )}
                {!l.owned && l.saved && (
                  <span data-testid="saved-badge" className="flex items-center gap-1 rounded-full bg-accent/15 text-accent px-2 py-0.5 font-semibold">
                    <Bookmark className="h-3 w-3 fill-current" /> Saved
                  </span>
                )}
                {l.language && l.language !== "English" && (
                  <span data-testid="lecture-language" className="flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 font-semibold">
                    <Globe className="h-3 w-3" /> {l.language}
                  </span>
                )}
              </div>

              <div className="mt-4 flex items-center gap-2 pt-4 border-t border-border">
                <button
                  data-testid="card-open-lecture"
                  onClick={(e) => { e.stopPropagation(); onOpen(l, "lecture"); }}
                  className="flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold hover:opacity-90 transition"
                >
                  <Play className="h-3 w-3" /> Lecture
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onOpen(l, "quiz"); }}
                  className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary/50 transition"
                >
                  Quiz
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onOpen(l, "guide"); }}
                  className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary/50 transition"
                >
                  Guide
                </button>

                <div className="ml-auto flex items-center gap-1">
                  {onLike && (
                    <button
                      data-testid="card-like-btn"
                      title={l.liked ? "Unlike" : "Like"}
                      onClick={(e) => { e.stopPropagation(); onLike(l); }}
                      className={`flex items-center gap-1 rounded-full px-2.5 h-8 text-xs font-bold transition ${l.liked ? "text-rose-600 bg-rose-500/10" : "text-muted-foreground hover:text-rose-600 hover:bg-rose-500/10"}`}
                    >
                      <Heart className={`h-4 w-4 ${l.liked ? "fill-current" : ""}`} />
                      {l.like_count > 0 && <span data-testid="card-like-count">{l.like_count}</span>}
                    </button>
                  )}
                  {onSave && !l.owned && (
                    <button
                      data-testid="card-save-btn"
                      title={l.saved ? "Remove from your library" : "Save to your library"}
                      onClick={(e) => { e.stopPropagation(); onSave(l); }}
                      className={`grid place-items-center h-8 w-8 rounded-full transition ${l.saved ? "text-accent bg-accent/15" : "text-muted-foreground hover:text-accent hover:bg-accent/10"}`}
                    >
                      <Bookmark className={`h-4 w-4 ${l.saved ? "fill-current" : ""}`} />
                    </button>
                  )}
                  {isInstructor && l.owned && (
                    <button
                      data-testid="card-move-btn"
                      title="Move to a class → chapter"
                      onClick={(e) => { e.stopPropagation(); setToMove(l); }}
                      className="grid place-items-center h-8 w-8 rounded-full text-muted-foreground hover:text-accent hover:bg-accent/10 transition"
                    >
                      <FolderInput className="h-4 w-4" />
                    </button>
                  )}
                  {onDelete && l.owned && (
                    <button
                      data-testid="card-delete-btn"
                      onClick={(e) => { e.stopPropagation(); setToDelete(l); }}
                      className="grid place-items-center h-8 w-8 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Delete lecture?</DialogTitle>
            <DialogDescription>
              "{toDelete?.title}" and its quiz scores will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              onClick={() => setToDelete(null)}
              className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary/50 transition"
            >
              Cancel
            </button>
            <button
              data-testid="confirm-delete-btn"
              onClick={() => { onDelete(toDelete.id); setToDelete(null); }}
              className="rounded-full bg-destructive text-destructive-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition"
            >
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MoveLectureDialog
        open={!!toMove}
        onOpenChange={(o) => !o && setToMove(null)}
        lecture={toMove}
        onMoved={() => { setToMove(null); if (onMoved) onMoved(); }}
      />
    </div>
  );
};
